import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../stores/authStore';
import { LoginBackground } from '../../../components/auth/LoginBackground';
import { withAreaPrefix } from '../../../lib/area-routes';
import { NativeButtonWithIcon } from '../../../components/common/NativeButtonWithIcon';

interface ForgotPasswordFormData {
  email: string;
}

export function ForgotPasswordPage() {
  const location = useLocation();
  const { t } = useTranslation('auth');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestPasswordReset = useAuthStore(state => state.requestPasswordReset);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError(null);
      await requestPasswordReset(data.email);
      setIsSubmitted(true);
    } catch {
      // Avoid leaking whether email exists; show generic error but still mark submitted
      setError(t('errors.reset_email_failed'));
      setIsSubmitted(true);
    }
  };

  if (isSubmitted) {
    return (
      <LoginBackground illustration="mail-sent">
        <div className="max-w-md w-full space-y-6 sm:space-y-8 p-6 sm:p-8 bg-violet-500/20 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20">
          <div className="text-center space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              {t('forgot_password.success_title')}
            </h2>
            <p className="text-sm text-white/80">{t('forgot_password.success_message')}</p>
            <Link
              to={withAreaPrefix('/auth/login', location.pathname)}
              className="mt-4 inline-block text-sm font-medium text-white hover:text-white/90 transition-colors duration-200 underline underline-offset-2"
            >
              {t('forgot_password.back_to_login')}
            </Link>
          </div>
        </div>
      </LoginBackground>
    );
  }

  return (
    <LoginBackground illustration="mail-sent">
      <div className="max-w-md w-full space-y-6 sm:space-y-8 p-6 sm:p-8 bg-violet-500/20 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20">
        <div className="space-y-3 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
            {t('forgot_password.title')}
          </h2>
          <p className="text-sm text-white/80">{t('forgot_password.subtitle')}</p>
        </div>
        <form className="mt-4 sm:mt-6 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="bg-red-500/30 border border-red-400/50 text-red-100 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-white/90">
              {t('forgot_password.email_label')}
            </label>
            <input
              {...register('email', {
                required: t('validation.email_required'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('validation.invalid_email'),
                },
              })}
              type="email"
              id="email"
              className="mt-1 block w-full px-3 py-2.5 border border-white/30 rounded-xl bg-white/15 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40"
              placeholder="you@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-200">{errors.email.message}</p>}
          </div>

          <div>
            <NativeButtonWithIcon
              type="submit"
              disabled={isSubmitting}
              icon="send"
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-violet-900 bg-white hover:bg-white/95 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 shadow-lg"
            >
              {isSubmitting
                ? t('forgot_password.submit_button_loading')
                : t('forgot_password.submit_button')}
            </NativeButtonWithIcon>
          </div>

          <div className="text-center text-sm">
            <Link
              to={withAreaPrefix('/auth/login', location.pathname)}
              className="font-medium text-white hover:text-white/90 transition-colors duration-200 underline underline-offset-2"
            >
              {t('forgot_password.back_to_login')}
            </Link>
          </div>
        </form>
      </div>
    </LoginBackground>
  );
}
